import {
	CIRCLE_SIZES,
	H3_CONFIG,
	MARKER_CONFIG
} from '@/composables/useMapGeometry';

type CIRCLE_SIZE = (typeof CIRCLE_SIZES)[keyof typeof CIRCLE_SIZES];

function calculatePolygonCenter(
	vertices: [number, number][]
): [number, number] {
	const totalVertices = vertices.length;
	const sumLat = vertices.reduce((sum, [lat]) => sum + lat, 0);
	const sumLng = vertices.reduce((sum, [, lng]) => sum + lng, 0);
	return [sumLat / totalVertices, sumLng / totalVertices];
}

export function metersToPixels(
	meters: number,
	lat: number,
	zoom: number
): number {
	try {
		const metersPerPixel =
			(H3_CONFIG.GOOGLE_MAPS_SCALE * Math.cos((lat * Math.PI) / 180)) /
			Math.pow(2, zoom);
		return (meters / metersPerPixel) * 2;
	} catch (error) {
		console.warn('Error calculating meters to pixels:', error);
		return 0;
	}
}

function greatCircleDistance(
	center: [number, number],
	vertex: [number, number]
): number {
	const [lat1, lng1] = center;
	const [lat2, lng2] = vertex;

	const dLat = ((lat2 - lat1) * Math.PI) / 180;
	const dLng = ((lng2 - lng1) * Math.PI) / 180;
	const a =
		Math.sin(dLat / 2) * Math.sin(dLat / 2) +
		Math.cos((lat1 * Math.PI) / 180) *
			Math.cos((lat2 * Math.PI) / 180) *
			Math.sin(dLng / 2) *
			Math.sin(dLng / 2);
	return (
		2 *
		Math.atan2(Math.sqrt(a), Math.sqrt(1 - a)) *
		H3_CONFIG.EARTH_RADIUS_METERS
	);
}

export function calculateSphericalPolygonArea(
	coordinates: [number, number][]
): number {
	if (!coordinates || coordinates.length < 3) return 0;

	const EARTH_RADIUS = 6378137;

	function toRadians(degrees: number): number {
		return degrees * (Math.PI / 180);
	}

	let area = 0;
	const n = coordinates.length;

	for (let i = 0; i < n; i++) {
		const j = (i + 1) % n;
		const [lat1, lng1] = coordinates[i] || [0, 0];
		const [lat2, lng2] = coordinates[j] || [0, 0];

		const lat1Rad = toRadians(lat1);
		const lat2Rad = toRadians(lat2);
		const deltaLng = toRadians(lng2 - lng1);

		area += deltaLng * (2 + Math.sin(lat1Rad) + Math.sin(lat2Rad));
	}

	return Math.abs((area * EARTH_RADIUS * EARTH_RADIUS) / 2);
}

export function getCircleRadius(count: number): CIRCLE_SIZE {
	const digitCount = String(count).length;

	switch (digitCount) {
		case 1:
			return CIRCLE_SIZES.SINGLE_DIGIT;
		case 2:
			return CIRCLE_SIZES.DOUBLE_DIGITS;
		case 3:
			return CIRCLE_SIZES.TRIPLE_DIGITS;
		case 4:
			return CIRCLE_SIZES.FOUR_DIGITS;
		default:
			return CIRCLE_SIZES.FIVE_OR_MORE_DIGITS;
	}
}

const DEFAULT_H3_AREA = 106295.74796178806;

export function calculateH3InscribedRadius(
	h3Boundary: [number, number][],
	zoom: number
): number {
	if (!h3Boundary || h3Boundary.length < 3) {
		return H3_CONFIG.DEFAULT_SIZE;
	}

	try {
		const center = calculatePolygonCenter(h3Boundary);

		const distances = h3Boundary
			.filter(vertex => vertex && vertex.length === 2)
			.map(([lat, lng]) => greatCircleDistance(center, [lat, lng]));

		if (distances.length === 0) {
			return H3_CONFIG.DEFAULT_SIZE;
		}

		const averageRadiusMeters =
			distances.reduce((sum, d) => sum + d, 0) / distances.length;
		const inscribedRadiusMeters = averageRadiusMeters * H3_CONFIG.APOTHEM_RATIO;

		const [centerLat] = center;
		const radiusPixels = metersToPixels(inscribedRadiusMeters, centerLat, zoom);

		return Math.max(
			H3_CONFIG.HEXAGON_MIN_SIZE,
			Math.min(H3_CONFIG.HEXAGON_MAX_SIZE, radiusPixels)
		);
	} catch {
		return H3_CONFIG.MIN_SIZE;
	}
}

export function areSameMapsValue(
	firstMap: google.maps.Map | null,
	secondMap: google.maps.Map | null
): boolean {
	const isObject = (val: google.maps.Map | null) =>
		val !== null && typeof val === 'object';
	const areBothNull = firstMap === null && secondMap === null;

	return (isObject(firstMap) && isObject(secondMap)) || areBothNull;
}

export function getCenterCoordinates(
	cluster: any
): { lat: number; lng: number } | null {
	if (!cluster.geometry?.coordinates) {
		return null;
	}

	const [lat, lng] = cluster.geometry.coordinates;
	return { lat, lng };
}

export function isClusterUnmapped(cluster: any): boolean {
	const pointCount = cluster.properties.pointCount;
	return !pointCount;
}

export function createClusterElement(
	content: number,
	className: string
): HTMLElement {
	const cluster = document.createElement('div');
	cluster.classList.add(className);
	cluster.textContent = content.toString();
	return cluster;
}

export function manipulateClusterClass(
	clusterInstance: any,
	action: 'add' | 'remove',
	className: string
): void {
	const element = clusterInstance?.content as Element;
	if (!element) return;

	if (action === 'add') {
		element.classList.add(className);
	} else {
		element.classList.remove(className);
	}
}

export function getColors(isHovered: boolean, isSelected: boolean) {
	const { WHITE, GREEN, DARK_GREEN, BLACK } = MARKER_CONFIG.COLORS;

	return {
		fill: isHovered ? GREEN : isSelected ? DARK_GREEN : WHITE,
		label: isHovered || isSelected ? WHITE : BLACK,
		shadow: isSelected || isHovered ? DARK_GREEN : BLACK
	};
}

export function getShadowConfig(isHovered: boolean, isSelected: boolean) {
	const { OPACITY } = MARKER_CONFIG.SHADOW;

	return {
		opacity: isSelected
			? OPACITY.SELECTED
			: isHovered
				? OPACITY.HOVER
				: OPACITY.DEFAULT,
		color: getColors(isHovered, isSelected).shadow
	};
}

export function calculateDimensions(
	circleRadius: number,
	shadowRadius: number
) {
	const padding = 8;
	const totalRadius = circleRadius + shadowRadius;
	const svgSize = totalRadius * 2 + padding;
	const center = svgSize / 2;

	return { svgSize, center, padding };
}
